import { useCallback, useEffect, useState } from 'react';
import { useEventCallback, useEventListener } from 'usehooks-ts';
const NO_LOCAL_STORAGE = typeof window === 'undefined' || !window.localStorage;
/**
 * Custom hook that uses the [`localStorage API`](https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage)
 * to persist state across page reloads.
 *
 * DO NOT USE THE HOOK DIRECTLY. Use derived hooks for *Core*, *Admin app* or *Warehouse app* instead.
 *
 * @template T - The type of the state to be stored in local storage.
 * @template K - The fixed constant of the key to be used for instance hooks.
 * @param {string} key - The key under which the value will be stored in local storage.
 * @param {T | (() => T)} initialValue - The initial value of the state or a function that returns the initial value.
 * @returns {[T, Dispatch<SetStateAction<T>>, () => void]} A tuple containing the stored value, a function to set the
 *   value and a function to remove the key from storage.
 * @example
 * ```tsx
 * // Access the `count` value, the `setCount` function to update it and `removeCount` function to remove the key from storage.
 * const [count, setCount, removeCount] = useLocalStorage('count', 0);
 * // Acts like a native useState from react.
 * setCount(prevValue => prevValue + 1)
 * ```
 */
export const useLocalStorageBase = (key, initialValue) => {
    // Get from local storage then
    // parse stored json or return initialValue
    const readValue = useCallback(() => {
        const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;
        // Prevent build error "window is undefined" but keep working
        if (NO_LOCAL_STORAGE) {
            return initialValueToUse;
        }
        try {
            const raw = window.localStorage.getItem(key);
            return raw ? JSON.parse(raw) : initialValueToUse;
        }
        catch (error) {
            console.warn(`Error reading localStorage key “${key}”:`, error);
            return initialValueToUse;
        }
    }, [initialValue, key]);
    const [storedValue, setStoredValue] = useState(() => {
        // if (initializeWithValue) {
        return readValue();
        // }
        // return initialValue instanceof Function ? initialValue() : initialValue
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = useEventCallback((value) => {
        // Prevent build error "window is undefined" but keeps working
        if (NO_LOCAL_STORAGE) {
            console.warn(`Tried setting localStorage key “${key}” but localStorage is not acceptable.`);
        }
        try {
            // Allow value to be a function so we have the same API as useState
            const newValue = value instanceof Function ? value(readValue()) : value;
            // Save to local storage
            window.localStorage.setItem(key, JSON.stringify(newValue));
            // Save state
            setStoredValue(newValue);
            // We dispatch a custom event so every similar useLocalStorage hook is notified
            window.dispatchEvent(new StorageEvent('local-storage', { key }));
        }
        catch (error) {
            console.warn(`Error setting localStorage key “${key}”:`, error);
        }
    });
    const removeValue = useEventCallback(() => {
        // Prevent build error "window is undefined" but keeps working
        if (NO_LOCAL_STORAGE) {
            console.warn(`Tried removing localStorage key “${key}” but localStorage is not acceptable`);
        }
        const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
        // Remove the key from local storage
        window.localStorage.removeItem(key);
        // Save state with default value
        setStoredValue(defaultValue);
        // We dispatch a custom event so every similar useLocalStorage hook is notified
        window.dispatchEvent(new StorageEvent('local-storage', { key }));
    });
    useEffect(() => {
        // if (key === undefined) return;
        setStoredValue(readValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    const handleStorageChange = useCallback((event) => {
        if (event.key && event.key !== key) {
            return;
        }
        setStoredValue(readValue());
    }, [key, readValue]);
    // this only works for other documents, not the current one
    // useEventListener('storage', handleStorageChange);
    // this is a custom event, triggered in writeValueToLocalStorage
    // See: useLocalStorage()
    useEventListener('local-storage', handleStorageChange);
    return [storedValue, setValue, removeValue];
};
